import type { RouteObject } from 'react-router-dom';
import { createBrowserRouter, Navigate } from 'react-router-dom';

import { funilPublicoHabilitado } from '@/config/funil';

import { Layout } from '@/components/Layout/Layout';
import { Login } from '@/pages/Login';
import { Signup } from '@/pages/Signup';
import { Planos } from '@/pages/Planos';
import { Billing } from '@/pages/Billing';
import { BillingSuccess } from '@/pages/BillingSuccess';
import { BillingCancel } from '@/pages/BillingCancel';
import { ConectarWordPress } from '@/pages/ConectarWordPress';
import { RequireAuth, RequireToken } from '@/components/RequireSessao';
import { Articles } from '@/pages/Articles';
import { ArticleEditor } from '@/pages/ArticleEditor';
import { NewArticleCreator } from '@/pages/NewArticleCreator';
import { AuditPanel } from '@/pages/AuditPanel';
import { Config } from '@/pages/Config';
import { ConfigAiProviders } from '@/pages/ConfigAiProviders';
import { ConfigAnalytics } from '@/pages/ConfigAnalytics';
import { ConfigCredenciais } from '@/pages/ConfigCredenciais';
import { ConfigCustos } from '@/pages/ConfigCustos';
import { ConfigExternalApis } from '@/pages/ConfigExternalApis';
import { ConfigHoroscopo } from '@/pages/ConfigHoroscopo';
import { ConfigPortalEsportes } from '@/pages/ConfigPortalEsportes';
import { ConfigQuentes } from '@/pages/ConfigQuentes';
import { ConfigMarca } from '@/pages/ConfigMarca';
import { ConfigPublicacao } from '@/pages/ConfigPublicacao';
import { ConfigSeo } from '@/pages/ConfigSeo';
import { ConfigCopiloto } from '@/pages/ConfigCopiloto';
import { ConfigSites } from '@/pages/ConfigSites';
import { ConfigConsumidores } from '@/pages/ConfigConsumidores';
import { Dashboard } from '@/pages/Dashboard';
import { Horoscopo } from '@/pages/Horoscopo';
import { NCNews } from '@/pages/NCNews';
import { Notifications } from '@/pages/Notifications';
import { Prompts } from '@/pages/Prompts';
import { Stories } from '@/pages/Stories';
import { StoryDetail } from '@/pages/StoryDetail';
import { TemplateEditor } from '@/pages/TemplateEditor';
import { Twitter } from '@/pages/Twitter';
import { Intelligence } from '@/pages/Intelligence';
import { Board } from '@/pages/Board';
import { Analytics } from '@/pages/Analytics';
import { Editorias } from '@/pages/Editorias';
import { Engineering } from '@/pages/Engineering';
import { Cidades } from '@/pages/Cidades';
import { EsteiraDaLauda } from '@/pages/EsteiraDaLauda';
import { Jornais } from '@/pages/Jornais';
import { Mapa } from '@/pages/Mapa';
import { TrocarSenha } from '@/pages/TrocarSenha';
import { ConfigUsuarios } from '@/pages/ConfigUsuarios';
import { FeedDrive } from '@/pages/FeedDrive';
import { Vigias } from '@/pages/Vigias';
import { Platform } from '@/pages/Platform';
import { Social } from '@/pages/Social';
import { PredictiveCalendar } from '@/pages/PredictiveCalendar';
import { ConfigPredictiveCalendar } from '@/pages/ConfigPredictiveCalendar';
import { InteractivePlaybook } from '@/pages/InteractivePlaybook';
import { StrategicBoard } from '@/pages/StrategicBoard';
import { DailyBlueprintHub } from '@/pages/DailyBlueprintHub';
import { ConfigStrategist } from '@/pages/ConfigStrategist';

import { Workflows2 } from '@/pages/Workflows2';
function RootErrorBoundary() {
  return (
    <div style={{ padding: '60px 20px', textAlign: 'center', fontFamily: 'var(--font-ui)', background: 'var(--bg-0)', color: 'var(--text-1)', minHeight: '100%' }}>
      <h2 style={{ fontSize: 'var(--fs-2xl)', color: 'var(--text-1)', marginBottom: '12px' }}>Página não encontrada (404)</h2>
      <p style={{ color: 'var(--text-3)', marginBottom: '24px' }}>A página ou o recurso solicitado não pôde ser encontrado.</p>
      {/* Levava para /artigos, que nao e a home: a rota indice redireciona para
          /inteligencia. Quem se perdia era devolvido a um lugar que nao e o
          comeco do produto. */}
      <a href="/inteligencia" style={{ display: 'inline-block', padding: '10px 20px', backgroundColor: 'var(--nexus-brand)', color: 'var(--text-on-accent)', borderRadius: 'var(--r-md)', textDecoration: 'none', fontWeight: 'bold' }}>
        Voltar ao início
      </a>
    </div>
  );
}

// W7.3 — as 4 telas do funil de receita só EXISTEM no roteador se a instância
// tiver vitrine. Não é redirect nem tela de "indisponível": rota que existe e
// responde alguma coisa já conta a história de que há um SaaS com tabela de
// preço do outro lado. Sem a rota, a SPA cai no 404 dela e não conta nada.
// A flag nasce desligada (`config/funil.ts`) e é de BUILD: instância de
// CLIENTE nunca é buildada com ela ligada. Par exato do backend, que com
// `FUNIL_PUBLICO_HABILITADO=false` nem monta `/planos` e `/signup`.
const funilLigado = funilPublicoHabilitado();

const rotasPublicasDoFunil: RouteObject[] = funilLigado
  ? [
      { path: '/signup', element: <Signup /> },
      { path: '/planos', element: <Planos /> },
    ]
  : [];

const rotasInternasDoFunil: RouteObject[] = funilLigado
  ? [
      { path: 'billing', element: <Billing /> },
      // Pousos do redirect do Stripe pós-checkout (success_url / cancel_url).
      // Dentro da trava do funil: instância de cliente não tem checkout, logo
      // não tem pouso de checkout.
      { path: 'billing/success', element: <BillingSuccess /> },
      { path: 'billing/cancel', element: <BillingCancel /> },
      { path: 'conectar-wordpress', element: <ConectarWordPress /> },
    ]
  : [];

export const router = createBrowserRouter([
  { path: '/login', element: <Login /> },
  { path: '/trocar-senha', element: <RequireToken><TrocarSenha /></RequireToken> },
  ...rotasPublicasDoFunil,
  {
    path: '/',
    element: (
      <RequireAuth>
        <Layout />
      </RequireAuth>
    ),
    errorElement: <RootErrorBoundary />,
    children: [
      { index: true, element: <Navigate to="/inteligencia" replace /> },
      { path: 'dashboard', element: <Dashboard /> },
      ...rotasInternasDoFunil,
      { path: 'inteligencia', element: <Intelligence /> },
      { path: 'inteligencia/blueprint', element: <DailyBlueprintHub /> },
      { path: 'inteligencia/playbook', element: <InteractivePlaybook /> },
      { path: 'inteligencia/kanban-estrategico', element: <StrategicBoard /> },
      { path: 'inteligencia/calendario', element: <PredictiveCalendar /> },
      { path: 'inteligencia/feeddrive', element: <FeedDrive /> },
      { path: 'vigias', element: <Vigias /> },
      { path: 'producao', element: <Board /> },
      { path: 'jornais', element: <Jornais /> },
      { path: 'jornais/:laudaId', element: <EsteiraDaLauda /> },
      { path: 'otimizacao', element: <Navigate to="/analytics?tab=seo" replace /> },
      { path: 'analytics', element: <Analytics /> },
      { path: 'engineering', element: <Engineering /> },
      { path: 'editorias', element: <Editorias /> },
      { path: 'cidades', element: <Cidades /> },
      { path: 'mapa', element: <Mapa /> },
      { path: 'stories', element: <Stories /> },
      { path: 'stories/:id', element: <StoryDetail /> },
      { path: 'artigos', element: <Articles /> },
      { path: 'artigos/novo', element: <NewArticleCreator /> },
      { path: 'artigos/:id', element: <ArticleEditor /> },
      // B-46: rotas /workflows e /alertas (execuções N8N) removidas —
      // 139 workflows, 0 ativos; o backend sai após checar chamadas vivas.
      { path: 'horoscopo', element: <Horoscopo /> },
      { path: 'ncnews', element: <NCNews /> },
      { path: 'twitter', element: <Twitter /> },
      { path: 'workflows2', element: <Workflows2 /> },
      { path: 'config', element: <Config /> },
      { path: 'config/estrategista', element: <ConfigStrategist /> },
      { path: 'config/calendario', element: <ConfigPredictiveCalendar /> },
      { path: 'config/sites', element: <ConfigSites /> },
      { path: 'config/consumidores', element: <ConfigConsumidores /> },
      { path: 'config/ai-providers', element: <ConfigAiProviders /> },
      { path: 'config/external-apis', element: <ConfigExternalApis /> },
      { path: 'config/analytics', element: <ConfigAnalytics /> },
      { path: 'config/seo', element: <ConfigSeo /> },
      { path: 'config/copiloto', element: <ConfigCopiloto /> },
      { path: 'config/publicacao', element: <ConfigPublicacao /> },
      { path: 'config/quentes', element: <ConfigQuentes /> },
      { path: 'config/usuarios', element: <ConfigUsuarios /> },
      { path: 'config/marca', element: <ConfigMarca /> },
      { path: 'config/horoscopo', element: <ConfigHoroscopo /> },
      { path: 'config/portal-esportes', element: <ConfigPortalEsportes /> },
      { path: 'config/credenciais', element: <ConfigCredenciais /> },
      { path: 'config/custos', element: <ConfigCustos /> },
      { path: 'config/prompts', element: <Prompts /> },
      { path: 'config/prompts/:id', element: <TemplateEditor /> },
      { path: 'config/notificacoes', element: <Notifications /> },
      { path: 'config/audit', element: <AuditPanel /> },
      { path: 'platform', element: <Platform /> },
      { path: 'social', element: <Social /> },
    ],
  },
]);
